/* Shared inspector panel for a selected GlobePoint.
   Used by both the 3D globe (GlobeCanvas) and the 2D flat map (FlatMapCanvas),
   so the same adopt / metadata UX renders regardless of mode. */
import type { GlobePoint } from "./types"

interface Props {
  selected: GlobePoint
  adopting: boolean
  adoptedIds: Set<string>
  onClose: () => void
  onAdopt: (p: GlobePoint) => void
}

export function AssetInspector({ selected, adopting, adoptedIds, onClose, onAdopt }: Props) {
  const owned = selected.owned || adoptedIds.has(selected.id)
  return (
    <div className="absolute top-3 left-3 w-[320px] ui-panel--glass p-4 pointer-events-auto">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="ui-header-title flex items-center gap-1.5">
            {selected.kind.toUpperCase()}
            {owned && <span className="text-[color:var(--fleet)]">★</span>}
          </div>
          <div className="text-[15px] font-semibold mt-0.5 leading-tight">{selected.label}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[color:var(--text-dim)] hover:text-[color:var(--text)] text-xl leading-none ml-2"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <dl className="text-xs space-y-1 text-[color:var(--text-muted)]">
        <Row k="Lat/Lng" v={`${selected.lat.toFixed(3)}, ${selected.lng.toFixed(3)}`} />
        {selected.severity && (
          <Row
            k="Severity"
            v={<span style={{ color: selected.color }}>{selected.severity}</span>}
          />
        )}
        {selected.confidence != null && (
          <Row k="Confidence" v={`${Math.round(selected.confidence * 100)}%`} />
        )}
        {selected.altitude_m != null && (
          <Row k="Altitude" v={`${Math.round(selected.altitude_m).toLocaleString()} m`} />
        )}
        {selected.velocity_kt != null && (
          <Row k="Speed" v={`${selected.velocity_kt.toFixed(1)} kt`} />
        )}
        {selected.heading != null && <Row k="Heading" v={`${Math.round(selected.heading)}°`} />}
        {selected.country && <Row k="Country" v={selected.country} />}
        {selected.mmsi && <Row k="MMSI" v={selected.mmsi} />}
      </dl>
      {selected.summary && (
        <div className="mt-2 text-xs text-[color:var(--text)] leading-relaxed">
          {selected.summary}
        </div>
      )}
      {(selected.kind === "flight" || selected.kind === "ship") && (
        <button
          type="button"
          disabled={adopting || adoptedIds.has(selected.id)}
          onClick={() => onAdopt(selected)}
          className={`mt-3 w-full text-xs font-semibold uppercase tracking-[0.14em] py-2 rounded transition ${
            adoptedIds.has(selected.id)
              ? "bg-[#facc15]/20 text-[#facc15] cursor-default"
              : "bg-[color:var(--accent)]/15 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/25"
          }`}
        >
          {adoptedIds.has(selected.id) ? "★ in your fleet" : "Adopt to fleet"}
        </button>
      )}
    </div>
  )
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[color:var(--text-dim)] uppercase text-[9px] tracking-widest self-center">
        {k}
      </span>
      <span className="text-[color:var(--text)]">{v}</span>
    </div>
  )
}
